
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  IconButton,
  Card,
  CardContent,
  Paper,
} from '@mui/material';
import { Plus, Minus, Loader2 } from 'lucide-react';
import { API_URL } from '../../../utils/apiConfig';

const INITIAL_CONTACT = {
  name: '',
  relationship: '',
  phone: '',
  email: ''
};

// Use REACT_APP_API_URL_DEVELOPMENT if available, otherwise fallback to REACT_APP_API_URL

  export default function EmergencyContactForm({ 
    fields, 
    responses, 
    employee, 
    formId, 
    employeeId,
    onSubmit,
    isCompleted 
  }) {
    const [contacts, setContacts] = useState([{ ...INITIAL_CONTACT }]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
  useEffect(() => {
    const fetchExistingContacts = async () => {
      if (!employeeId) {
        console.log('No employeeId provided, skipping fetch');
        return;
      }

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found');
        }

        const response = await fetch(`${API_URL}/employees/${employeeId}/emergency_contacts`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Server returned ${response.status}: ${errorText}`);
        }
        
        const data = await response.json();
        
        if (Array.isArray(data) && data.length > 0) {
          setContacts(data);
        }
      } catch (err) {
        console.error('Error fetching emergency contacts:', err);
        setError(`Failed to load existing contacts: ${err.message}`);
        setContacts([{ ...INITIAL_CONTACT }]);
      }
    };

    fetchExistingContacts();
  }, [employeeId]);

  const handleAddContact = () => {
    if (contacts.length < 3) {
      setContacts(prev => [...prev, { ...INITIAL_CONTACT }]);
    }
  };

  const handleRemoveContact = (index) => {
    if (contacts.length > 1) {
      setContacts(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleChange = (index, field, value) => {
    setContacts(prev => 
      prev.map((contact, i) => 
        i === index ? { ...contact, [field]: value } : contact
      )
    );
  };

  const validateContacts = () => {
    if (!contacts[0]?.name || !contacts[0]?.relationship || !contacts[0]?.phone) {
      setError('First emergency contact requires name, relationship, and phone number');
      return false;
    }

    for (let i = 1; i < contacts.length; i++) {
      const contact = contacts[i];
      const hasPartialInfo = contact.name || contact.relationship || contact.phone;
      const hasMissingRequired = !contact.name || !contact.relationship || !contact.phone;
      
      if (hasPartialInfo && hasMissingRequired) {
        setError(`Contact #${i + 1} is incomplete. Please fill all required fields or remove it.`);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    setError(null);
    setSuccess(false);

    if (!validateContacts()) {
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const validContacts = contacts.filter(contact => 
        contact.name || contact.relationship || contact.phone
      );

      // Save emergency contacts
      const contactsResponse = await fetch(`${API_URL}/employees/${employeeId}/emergency_contacts`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ contacts: validContacts }),
      });

      if (!contactsResponse.ok) {
        const errorText = await contactsResponse.text();
        throw new Error(`Failed to save contacts: ${errorText}`);
      }

      const updatedContacts = await contactsResponse.json();
      setContacts(updatedContacts.length > 0 ? updatedContacts : [{ ...INITIAL_CONTACT }]);
      
      // Call the onSubmit prop to update form status in parent components
      if (onSubmit) {
        await onSubmit({
          contacts: validContacts,
          status: 'completed'
        });
      }

      setSuccess(true);
    } catch (err) {
      console.error('Error saving contacts:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      maxWidth: 800, 
      margin: '0 auto',
      py: 4,
    }}>
     

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            '& .MuiAlert-message': {
              fontSize: '0.95rem'
            }
          }}
        >
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 3,
            '& .MuiAlert-message': {
              fontSize: '0.95rem'
            }
          }}
        >
          Emergency contacts saved successfully!
        </Alert>
      )}

      {isCompleted && (
        <Alert 
          severity="info" 
          sx={{ 
            mb: 3,
            '& .MuiAlert-message': {
              fontSize: '0.95rem'
            }
          }}
        >
          This form has been completed. You can still make updates if needed.
        </Alert>
      )}

      {contacts.map((contact, index) => (
        <Card 
          key={index} 
          elevation={0}
          sx={{ 
            mb: 3, 
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ 
              p: 2.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
            }}>
              <Typography 
                variant="h6"
                sx={{
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  color: 'text.primary'
                }}
              >
                Contact #{index + 1}
                {index === 0 && (
                  <Typography 
                    component="span" 
                    sx={{ 
                      ml: 1,
                      color: 'error.main',
                      fontSize: '0.875rem',
                      fontWeight: 400
                    }}
                  >
                    (Required)
                  </Typography>
                )}
              </Typography>
              {index > 0 && (
                <IconButton
                  onClick={() => handleRemoveContact(index)}
                  sx={{ 
                    color: 'text.secondary',
                    '&:hover': {
                      color: 'error.main',
                      bgcolor: 'error.lighter'
                    }
                  }}
                >
                  <Minus size={18} />
                </IconButton>
              )}
            </Box>

            <Box sx={{ p: 3 }}>
              <TextField
                label="Full Name"
                value={contact.name || ''}
                onChange={(e) => handleChange(index, 'name', e.target.value)}
                required={index === 0}
                fullWidth
                variant="outlined"
                sx={{
                  mb: 2.5,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'grey.50',
                    '&:hover': {
                      bgcolor: 'background.paper',
                    },
                    '&.Mui-focused': {
                      bgcolor: 'background.paper',
                    }
                  }
                }}
                error={index === 0 && !contact.name}
                helperText={index === 0 && !contact.name ? 'Name is required' : ''}
              />

              <TextField
                label="Relationship"
                value={contact.relationship || ''}
                onChange={(e) => handleChange(index, 'relationship', e.target.value)}
                required={index === 0}
                fullWidth
                variant="outlined"
                sx={{
                  mb: 2.5,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'grey.50',
                    '&:hover': {
                      bgcolor: 'background.paper',
                    },
                    '&.Mui-focused': {
                      bgcolor: 'background.paper',
                    }
                  }
                }}
                error={index === 0 && !contact.relationship}
                helperText={index === 0 && !contact.relationship ? 'Relationship is required' : ''}
              />

              <TextField
                label="Phone Number"
                value={contact.phone || ''}
                onChange={(e) => handleChange(index, 'phone', e.target.value)}
                required={index === 0}
                fullWidth
                variant="outlined"
                sx={{
                  mb: 2.5,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'grey.50',
                    '&:hover': {
                      bgcolor: 'background.paper',
                    },
                    '&.Mui-focused': {
                      bgcolor: 'background.paper',
                    }
                  }
                }}
                error={index === 0 && !contact.phone}
                helperText={index === 0 && !contact.phone ? 'Phone number is required' : ''}
              />

<TextField
  label="Email Address"
  value={contact.email || ''}
  onChange={(e) => handleChange(index, 'email', e.target.value)}
  fullWidth
  variant="outlined"
  sx={{
    mb: 2.5,
    '& .MuiOutlinedInput-root': {
      bgcolor: 'grey.50',
      '&:hover': {
        bgcolor: 'background.paper',
      },
      '&.Mui-focused': {
        bgcolor: 'background.paper',
      }
    }
  }}
/>
            </Box>
          </CardContent>
        </Card>
      ))}

      {contacts.length < 3 && (
        <Button
          startIcon={<Plus size={18} />}
          onClick={handleAddContact}
          variant="outlined"
          sx={{ 
            mb: 2,
            borderWidth: 2,
            '&:hover': {
              borderWidth: 2
            }
          }}
        >
          Add Another Contact
        </Button>
      )}

      <Button
        onClick={handleSubmit}
        variant="contained"
        disabled={loading}
        fullWidth
        sx={{ 
          mt: 2,
          py: 1.5,
          bgcolor: 'primary.main',
          '&:hover': {
            bgcolor: 'primary.dark',
          }
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Loader2 size={18} className="animate-spin" />
            <Box component="span" sx={{ ml: 1 }}>Saving...</Box>
          </Box>
        ) : (
          'Save All Contacts'
        )}
      </Button>

      <Typography 
        variant="caption" 
        sx={{ 
          display: 'block', 
          mt: 2.5,
          textAlign: 'center',
          color: 'text.secondary'
        }}
      >
        * First emergency contact is mandatory with name, relationship, and phone number
      </Typography>
    </Box>
  );
}